import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { characterKeys } from '../characters/queries'
import { toEnvelope, type CharacterEnvelope } from '../characters/mutations'
import type { DraftPreview, DraftStatus, DraftSummary } from './types'

type Schema = components['schemas']

export const draftKeys = {
  all: ['drafts'] as const,
  lists: () => [...draftKeys.all, 'list'] as const,
  list: (status: DraftStatus | 'all') => [...draftKeys.lists(), status] as const,
  previews: () => [...draftKeys.all, 'preview'] as const,
  preview: (id: string) => [...draftKeys.previews(), id] as const,
}

/**
 * The caller's drafts.
 *
 * Defaults to `Pending` because that is the only status anyone opens this for: a draft that has
 * been applied or turned down is history, and history has a home on the character sheet rather
 * than in a to-do list.
 */
export function useDrafts(status: DraftStatus | 'all' = 'Pending'): UseQueryResult<DraftSummary[]> {
  return useQuery({
    queryKey: draftKeys.list(status),
    queryFn: () =>
      api.get<DraftSummary[]>('/character-drafts', {
        query: status === 'all' ? undefined : { status },
      }),
  })
}

/**
 * One draft, with the diff it would produce.
 *
 * **The diff is not computed here.** "What would change" is a question about the character's
 * current state, and answering it client-side would mean fetching the sheet, joining it against
 * the payload and interpreting the result — badly, and differently from every other place that
 * tries. The server answers it once. Same division as the mutation envelope: the server decides,
 * the client renders.
 */
export function useDraftPreview(id: string | null): UseQueryResult<DraftPreview> {
  return useQuery({
    queryKey: draftKeys.preview(id ?? ''),
    enabled: id !== null,
    queryFn: () => api.get<DraftPreview>(`/character-drafts/${id}`),
  })
}

/**
 * Applies a draft. This is the only call in the SPA that turns a proposal into a character.
 *
 * The response is a `CharacterEnvelope`, so the sheet cache is patched exactly as any other
 * mutation patches it — no refetch. Both list caches are invalidated because the draft has left
 * `Pending` and, for a creation, a character that did not exist now does.
 *
 * Failures are left to the caller. The interesting ones are not "the network broke": a 409 means
 * the character changed after the draft was prepared, and a 410 means the draft is no longer on
 * offer. Both want to be read, not retried.
 */
export function useApplyDraft() {
  const queryClient = useQueryClient()

  return useMutation<CharacterEnvelope, unknown, string>({
    mutationFn: async (id) => {
      const dto = await api.post<Schema['CharacterEnvelope']>(`/character-drafts/${id}/apply`)
      return toEnvelope(dto)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: draftKeys.all })
      void queryClient.invalidateQueries({ queryKey: characterKeys.all })
    },
  })
}

/** Turns a draft down. Nothing is written to any character, so only the draft caches move. */
export function useRejectDraft() {
  const queryClient = useQueryClient()

  return useMutation<DraftSummary, unknown, string>({
    mutationFn: (id) => api.post<DraftSummary>(`/character-drafts/${id}/reject`),
    onSuccess: (draft) => {
      if (draft.id !== undefined) {
        queryClient.setQueryData(
          draftKeys.preview(draft.id),
          (previous: DraftPreview | undefined) =>
            previous === undefined ? previous : { ...previous, draft, canApply: false },
        )
      }
      void queryClient.invalidateQueries({ queryKey: draftKeys.lists() })
    },
  })
}
